/**
 * App - Main application component with React Router
 *
 * ROUTES:
 * - /login - Login/Verify flow
 * - /welcome - Hub with 6 buttons (protected)
 * - /messages - Messaging screen (protected)
 * - /gallery - Coming Soon (protected)
 * - /account - Logout screen (protected)
 * - /vault - Coming Soon (protected)
 * - /groupchat - Coming Soon (protected)
 * - /poke - Coming Soon (protected)
 */

import { SUPPORT_EMAIL, TWITTER_HANDLE } from '@/config/brandConfig'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from '@/features/auth'
import { ProtectedRoute } from '@/components/layout/ProtectedRoute'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { ToastProvider } from '@/components/ui/ToastContainer'

// Initialize Stripe with publishable key from environment variable
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_YOUR_DEFAULT_KEY_HERE')

// Auth screens
import { LoginScreen } from '@/components/auth/LoginScreen'
import { VerifyCodeScreen } from '@/components/auth/VerifyCodeScreen'

// Pages
import { WelcomeScreen } from '@/pages/WelcomeScreen'
import { MessagingPage } from '@/pages/MessagingPage'
import { AccountPage } from '@/pages/AccountPage'
import { GalleryPage } from '@/pages/GalleryPage'
import { VaultPage } from '@/pages/VaultPage'
import { GroupChatPage } from '@/pages/GroupChatPage'
import { PokePage } from '@/pages/PokePage'
import { ApplyPage } from '@/pages/ApplyPage'
// ComingSoonPage removed - not currently used
// PaymentMethodsPage removed - wallet moved into AccountPage
import { SubscriptionPage } from '@/pages/SubscriptionPage'
import NewsroomPage from '@/features/newsroom/NewsroomPage'
import { VipPage } from '@/pages/VipPage'
import { StatusPage } from '@/pages/StatusPage'
import { InfoPage } from '@/pages/InfoPage'
import { LegalPage } from '@/pages/LegalPage'

// [MOONS] Success page after Stripe Checkout redirect — only used in Moonwrigh
function MoonPurchaseSuccess() {
  const [countdown, setCountdown] = useState(3)

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          window.location.href = '/messages'
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center px-6">
        <div className="text-6xl mb-4">✓</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Store Credit Added!</h1>
        <p className="text-gray-600 mb-4">Your store credit has been added to your balance.</p>
        <p className="text-sm text-gray-400">Redirecting in {countdown}...</p>
      </div>
    </div>
  )
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth()
  const [emailForVerification, setEmailForVerification] = useState<string | null>(null)

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Auth Routes - Public */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to="/welcome" replace />
          ) : emailForVerification ? (
            <VerifyCodeScreen
              email={emailForVerification}
              onBack={() => setEmailForVerification(null)}
            />
          ) : (
            <LoginScreen onCodeSent={setEmailForVerification} />
          )
        }
      />

      {/* Gallery - Public but shows different content based on auth */}
      <Route path="/gallery" element={<GalleryPage />} />

      {/* Apply for Access - Public (for users who don't have access yet) */}
      <Route path="/apply" element={<ApplyPage />} />

      {/* VIP - Public (no auth required) */}
      <Route path="/vip" element={<VipPage />} />

      {/* Status - Public (password-gated VIP dashboard) */}
      <Route path="/status" element={<StatusPage />} />

      {/* Info - Public (Stripe business info page) */}
      <Route path="/info" element={<InfoPage />} />

      {/* Legal - Public: Terms & Conditions, Privacy Policy, Refund Policy (linked from the checkout) */}
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/refund-policy" element={<LegalPage kind="refunds" />} />

      {/* Protected Routes - Require Authentication */}
      <Route element={<ProtectedRoute />}>
        <Route path="/welcome" element={<WelcomeScreen />} />
        <Route path="/messages" element={<MessagingPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/vault" element={<VaultPage />} />
        <Route path="/newsroom" element={<NewsroomPage />} />
        <Route path="/wallet" element={<Navigate to="/status" replace />} />
        {/* Legacy routes for backward compatibility */}
        <Route path="/payment-methods" element={<Navigate to="/account" replace />} />
        <Route path="/groupchat" element={<GroupChatPage />} />
        <Route path="/poke" element={<PokePage />} />
        {/* [MOONS] Moon purchase redirect routes — only in Moonwrigh */}
        <Route path="/moons/success" element={<MoonPurchaseSuccess />} />
        <Route path="/moons/cancel" element={<Navigate to="/messages" replace />} />
      </Route>

      {/* Default Route */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/welcome" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* 404 - Redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MESSAGE
  const isMaintenanceActive = maintenanceMode && maintenanceMode !== '0'
  const isDefaultMessage = maintenanceMode === '1' || maintenanceMode === 'true'
  const maintenanceText = isDefaultMessage
    ? 'This site is currently unavailable. Please check back shortly.'
    : maintenanceMode

  if (isMaintenanceActive) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white flex items-center justify-center">
        <div className="text-center px-6 max-w-xl">
          <div className="mx-auto mb-8 w-12 h-12 relative animate-spin" style={{ animationDuration: '1s', animationTimingFunction: 'steps(10, end)' }}>
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="absolute left-1/2 top-0 w-[2.5px] h-[14px] -ml-[1.25px] rounded-full origin-[center_24px]"
                style={{
                  transform: `rotate(${i * 36}deg)`,
                  backgroundColor: `rgba(120, 120, 128, ${1 - i * 0.08})`,
                }}
              />
            ))}
          </div>

          <h1 className="text-2xl sm:text-4xl font-semibold text-gray-800 mb-4">I'll be back soon.</h1>
          <p className={`text-gray-500 text-sm sm:text-lg mb-6 ${isDefaultMessage ? 'whitespace-nowrap' : ''}`}>{maintenanceText}</p>
          <hr className="border-gray-200 mb-6" />
          <p className="text-gray-500 text-xs sm:text-sm mb-1 whitespace-nowrap">
            If you need immediate assistance, please email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-500 hover:text-sky-600 underline underline-offset-2">{SUPPORT_EMAIL}</a>
          </p>
          <p className="text-gray-500 text-xs sm:text-sm whitespace-nowrap">
            or check{' '}
            <a href={`https://twitter.com/${TWITTER_HANDLE}`} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-600 underline underline-offset-2">@{TWITTER_HANDLE}</a>
            {' '}for live updates.
          </p>
        </div>
      </div>
    )
  }

  return (
    <ToastProvider>
      <Elements stripe={stripePromise}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </Elements>
    </ToastProvider>
  )
}

export default App
